import { useState } from 'react';
import { Alert, Segmented, Skeleton, StatusHint } from '@indipia/ui-core';
import { KakaoMessageCard } from '@indipia/shared-features';
import type { PreviewState } from '../hooks/usePhotoSharePreview';
import type { SharePreviewGroup } from '../api/shareAdapter';

// 사진 공유 알림톡 미리보기 — 서버(Hermes 렌더)가 준 문구·버튼을 그대로 보여준다. 프론트에서 문구를 조립하지 않는다(확정 결정 36·39·40).
// 수신자 유형 전환은 Segmented, 호출 지연은 Skeleton, 오류는 Alert.

const GROUP_OPTIONS = [
  { label: '고객', value: 'CUSTOMER' },
  { label: '반장·거래처', value: 'GENERAL' },
];

interface Props {
  customer: PreviewState;
  general: PreviewState;
  /** 고객 미리보기 대상(현장 고객명) 유무 */
  hasCustomer: boolean;
  generalRecipientName: string | null;
  /** 일반 미리보기에 필요한 제목·내용이 채워졌는지 */
  generalInputReady: boolean;
  hasPhotos: boolean;
}

export function SharePreviewPanel({ customer, general, hasCustomer, generalRecipientName, generalInputReady, hasPhotos }: Props) {
  const [group, setGroup] = useState<SharePreviewGroup>('CUSTOMER');
  const state = group === 'CUSTOMER' ? customer : general;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Segmented
        size="sm"
        options={GROUP_OPTIONS}
        value={group}
        onChange={(v) => setGroup(v as SharePreviewGroup)}
      />

      {!hasPhotos && <StatusHint tone="muted">공유할 사진을 선택하면 미리보기가 표시됩니다.</StatusHint>}
      {hasPhotos && group === 'CUSTOMER' && !hasCustomer && (
        <StatusHint tone="muted">이 현장에 등록된 고객이 없어 고객 미리보기를 표시할 수 없습니다.</StatusHint>
      )}
      {hasPhotos && group === 'GENERAL' && !generalRecipientName && (
        <StatusHint tone="muted">반장·거래처 수신자를 추가하면 미리보기가 표시됩니다.</StatusHint>
      )}
      {hasPhotos && group === 'GENERAL' && generalRecipientName && !generalInputReady && (
        <StatusHint tone="muted">제목·내용·링크 유효기간을 입력하면 미리보기가 표시됩니다.</StatusHint>
      )}
      {hasPhotos && group === 'GENERAL' && generalRecipientName && generalInputReady && (
        <StatusHint tone="muted">{generalRecipientName} 기준</StatusHint>
      )}

      {state.status === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Skeleton height={44} />
          <Skeleton height={140} />
          <Skeleton height={36} />
        </div>
      )}

      {state.status === 'error' && <Alert kind="error">{state.error}</Alert>}

      {state.status === 'ready' && state.data && (
        <KakaoMessageCard
          content={state.data.body}
          buttons={state.data.buttons.map((b) => b.name)}
        />
      )}
    </div>
  );
}
